import { useContext, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import GetIpv4 from '@src/services/hooks/getIpV4/useGetIpv4';

import { getHomeRouteForLoggedInUser, isObjEmpty } from '@src/utility/Utils';
import { toastError, toastSuccess } from '@src/utility/Toast';
import axios, { AxiosError } from 'axios';
import isSigned from '@src/services/hooks/VerificationSigningTerms/useVerificationSigningTerms';
import { AuthenticationFormData } from './AuthenticationFormData';

type useAuthenticationFormProps = {
  onChangeBlocking: (isBlocking: boolean) => void;
  onChangeBlockingResend: (isBlocking: boolean) => void;
};

export default function useAuthenticationForm({
  onChangeBlocking,
  onChangeBlockingResend,
}: useAuthenticationFormProps) {
  const history = useHistory();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        code: yup
          .string()
          .required('O código é obrigatório.')
          .min(6, 'Código deve ter no mínimo 6 digítos.')
          .max(6, 'Código deve ter no máximo 6 digítos'),
      }),
    []
  );

  const { register, handleSubmit, errors, control } =
    useForm<AuthenticationFormData>({
      resolver: yupResolver(validationSchema),
    });

  const checkCode = async (code: string) => {
    const api = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
    });

    const userData = localStorage.getItem('userData') ?? '{}';
    const user = JSON.parse(userData);

    const body = {
      email: user.email,
      code,
    };
    let tokenRecaptcha = null;
    let ip_address = null;
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
    } else {
      tokenRecaptcha = await executeRecaptcha('2FA');
      ip_address = await GetIpv4();
    }

    await api.post('2fa/check-code', {
      email: body.email,
      code: body.code,
      token_recaptcha: tokenRecaptcha,
      ip: ip_address,
    });

    user.requires2faAuth = false;
    user.two_factor_authentication = 1;
    const userJson = JSON.stringify(user);
    localStorage.setItem('userData', userJson);
  };

  const onSubmit = async (formValues: AuthenticationFormData) => {
    if (isObjEmpty(errors)) {
      try {
        onChangeBlocking(true);

        await checkCode(formValues.code);

        onChangeBlocking(false);

        const userData = localStorage.getItem('userData') ?? '{}';
        const user = JSON.parse(userData);

        const token = localStorage.getItem('accessToken') || '';
        const data = await isSigned(token);
        if (!data.isSigned && !user.isTheSupport) {
          // if (user.iduser === data.idrepresentative) {
          //   history.push('signing-of-terms');
          // } else {
          //   history.push('notice-of-terms');
          // }
        } else {
          history.push(getHomeRouteForLoggedInUser(user.role));
        }
      } catch (error) {
        onChangeBlocking(false);

        let message = null;

        const err = error as Error | AxiosError;

        if (axios.isAxiosError(err)) {
          message =
            err.response?.data.message || 'Algo deu errado ao efetuar o login';
        } else {
          message = 'Server error';
        }

        toastError({ title: 'Atenção', description: message });
      }
    }
  };

  const onClick = async () => {
    onChangeBlockingResend(true);

    try {
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
      });

      const userData = localStorage.getItem('userData') ?? '';

      const user = JSON.parse(userData);

      const body = {
        email: user.email,
        limitResend: 1,
      };

      await api.post('2fa/send-email', body);

      toastSuccess({
        title: 'Sucesso',
        description: 'Código reenviado',
      });
    } catch (error) {
      let message = null;

      const err = error as AxiosError | Error;

      if (axios.isAxiosError(err)) {
        message =
          err.response?.data.message || 'Algo deu errado ao efetuar o login';
      } else {
        message = 'Server error';
      }

      toastError({ title: 'Atenção', description: message });
    }

    onChangeBlockingResend(false);
  };

  return {
    register,
    errors,
    onSubmit: handleSubmit(onSubmit),
    onClick,
    control,
  };
}
